import React, { useEffect, useRef, useState } from "react";
import styles from "./JobDatabaseTable.module.scss";
import TNLoader from "../../../reusable-components/TNLoader";
import CustomTableWithPagination from "../../../reusable-components/CustomTable";
import {
  getDatabaseColumns,
  getTableFooter,
  JobDatabaseTableConsts,
} from "./constants";
import { generatePath, useHistory } from "react-router-dom";
import { appRoutes } from "../../../constants/appRoutes";
import JobDatabaseService from "../../../services/jobDatabase.service";
import ConditionalRender from "../../../reusable-components/ConditionalRender";
import { JobFilterOptionPayloadType } from "../../../Types/jobDatabaseTypes";
import { storageKeys } from "../../../constants/storageKeys";
import RolesServices from "../../../services/roles.services";
import { UserRoleActions } from "../../../enums/UserRolesEnum";
import { message, Modal } from "antd";
import { useJobDatabaseContext } from "../../../context/JobDatabaseFilterContext";
import { ApiResponse } from "../../../enums/ApiResponse";
import { CommonButtonTexts } from "../../../enums/ButtonAttributesEnums";

interface JobDatabaseTableProps {
  handleArchiveConfirmOpen: (record: any) => void;
  searchValue?: string | undefined;
  jobDatabaseFilters?: JobFilterOptionPayloadType;
  jobDatabaseSort: Object;
  handleSorterAction?: (record: JobFilterOptionPayloadType) => void;
  handleJobProfileNavigate?: (record) => void;
}

const JobDatabaseTable = ({
  handleArchiveConfirmOpen,
  searchValue,
  jobDatabaseFilters,
  jobDatabaseSort,
  handleSorterAction,
  handleJobProfileNavigate
}: JobDatabaseTableProps) => {
  const navigate = useHistory();
  const isMountRef = useRef<boolean>(true);
  const {
    NO_RECORDS,
    NO_RECORDS_FOUND,
    NO_ACCESS_CUSTOMER_DB,
    BLOCK_MSG,
    BLOCK_TITLE,
    UNBLOCK_TITLE,
    UNBLOCK_MSG,
  } = JobDatabaseTableConsts;
  const {
    FetchJobDatabaseEntries,
    GetPaginationDefaultMeta,
    BlockUnblockJobs,
  } = JobDatabaseService();
  const [pageSizeChanged, setPageSizeChanged] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(
    Number(sessionStorage.getItem(storageKeys?.JOB_DB_PAGESIZE) ?? 10),
  );
  const [confirmationModalOpen, setConfirmationModalOpen] =
    useState<boolean>(false);
  const [selectedJobId, setSelectedJobId] = useState<string | undefined>(
    undefined,
  );

  const userDetails = localStorage.getItem(storageKeys.LS_USER_DETAILS);
  const userDetailsJson = JSON.parse(userDetails);
  const userRoleInfo = userDetailsJson?.[storageKeys?.USER_ROLE_KEY];
  const userRoleProfileInfo = userDetailsJson?.[storageKeys?.USER_PROFILE_KEY];

  const { FetchUserRoleDetails } = RolesServices();
  const { data: roleDetails } = FetchUserRoleDetails(userRoleInfo);
  const { mutateAsync: blockUnblockJob, isPending: blockUnblockPending } =
    BlockUnblockJobs();
  const { data: defaultData, isFetching: gettingDefaults } =
    GetPaginationDefaultMeta();

  const handleConfirmationModalOpen = () => setConfirmationModalOpen(true);
  const handleConfirmationModalClose = () => {
    setConfirmationModalOpen(false);
    setSelectedJobId(undefined);
  };

  const handlePageChange = (current: number) => {
    setCurrentPage(current);
    sessionStorage.setItem(
      storageKeys.JOB_DB_PAGE,
      JSON.stringify({ page: current }),
    );
  };

  const handlePageSizeChange = (size: number) => {
    setCurrentPage(1);
    setPageSize(size);
    setPageSizeChanged(true);
    try {
      sessionStorage.setItem(storageKeys.JOB_DB_PAGESIZE, size);
    } catch (err) {
      console.error("Err", err);
    }
  };

  const customerDatabaseAccess = !!userRoleProfileInfo
    ? roleDetails?.[0]?.otherData?.[2]?.multiPannel?.[0]?.selectedOptions?.includes(
        UserRoleActions.ALLOW,
      )
    : true;

  const {
    data: jobList,
    isFetching,
    isRefetching,
    refetch,
    isSuccess: listFetchSuccess,
  } = FetchJobDatabaseEntries(
    {
      page: currentPage,
      perPage: pageSize,
      search: jobDatabaseFilters?.search,
      jobNumber: jobDatabaseFilters?.jobNumber ?? [],
      jobName: jobDatabaseFilters?.jobName ?? [],
      companyId: jobDatabaseFilters?.companyId ?? [],
      jobType: jobDatabaseFilters?.jobType ?? [],
      equipment: jobDatabaseFilters?.equipment ?? [],
      materialCategory: jobDatabaseFilters?.materialCategory ?? [],
      materialType: jobDatabaseFilters?.materialType ?? [],
      salesPersonId: jobDatabaseFilters?.salesPersonId ?? [],
      isBlocked: jobDatabaseFilters?.isBlocked,
      sortBy: jobDatabaseSort?.sortBy,
      sortOrder: jobDatabaseSort?.sortOrder,
    },
    pageSizeChanged,
    !!sessionStorage.getItem(storageKeys.JOB_DB_PAGESIZE),
  );

  const handleCustomerProfileNavigate = (
    customerName: string,
    customerId: string,
  ) => {
    const locationProp = {
      customerName: customerName,
      navigatedFromQuote: false,
    };
    if (customerDatabaseAccess)
      navigate.push(
        generatePath(appRoutes.CUSTOMER_PROFILE_ID, { id: customerId }),
        locationProp,
      );
    else message.error(NO_ACCESS_CUSTOMER_DB);
  };

  const handleBlockUnblockJob = (record: any) => {
    setSelectedJobId(record?.id);
    handleConfirmationModalOpen();
  };

  const handleBlockUnblockAction = async () => {
    await blockUnblockJob(
      {
        jobId: selectedJobId,
        blockAction: !!!jobDatabaseFilters?.isBlocked,
      },
      {
        onSuccess: () => {
          refetch();
        },
      },
    );
    handleConfirmationModalClose();
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchValue, jobDatabaseFilters]);

  useEffect(() => {
    if (jobList?.meta?.perPage !== pageSize && !!jobList?.meta?.perPage)
      setCurrentPage(1);
  }, [pageSize, jobList?.meta?.perPage]);

  useEffect(() => {
    const storageItem = sessionStorage.getItem(storageKeys.JOB_DB_PAGE);
    if (!!storageItem) {
      const pageValue = JSON.parse(storageItem);
      setCurrentPage(pageValue?.page);
    }
    const storageItemSize = sessionStorage.getItem(storageKeys.JOB_DB_PAGESIZE);
    if (!!storageItemSize) {
      const pageSizeValue = JSON.parse(storageItemSize);
      setPageSize(pageSizeValue);
    }
  }, []);

  useEffect(() => {
    if (isMountRef.current) {
      isMountRef.current = false;
      return;
    }
    refetch();
  }, [jobDatabaseFilters, currentPage, pageSize]);

  useEffect(() => {
    if (
      !!defaultData?.message &&
      defaultData?.message === ApiResponse.SUCCESS
    ) {
      setPageSize(defaultData?.data?.jobDefaultPageSize);
      sessionStorage.setItem(
        storageKeys.JOB_DB_PAGESIZE,
        defaultData?.data?.jobDefaultPageSize,
      );
    }
  }, [defaultData]);

  useEffect(() => {
    if (pageSizeChanged && listFetchSuccess) {
      setPageSizeChanged(false);
    }
  }, [pageSizeChanged, listFetchSuccess]);

  return (
    <>
      <TNLoader
        isLoading={
          isFetching || isRefetching || gettingDefaults || blockUnblockPending
        }
      />
      <Modal
        visible={confirmationModalOpen}
        title={jobDatabaseFilters?.isBlocked ? UNBLOCK_TITLE : BLOCK_TITLE}
        okText={CommonButtonTexts.CONFIRM}
        onOk={handleBlockUnblockAction}
        onCancel={handleConfirmationModalClose}
      >
        {jobDatabaseFilters?.isBlocked ? UNBLOCK_MSG : BLOCK_MSG}
      </Modal>
      <ConditionalRender
        visible={jobList?.data?.length > 0}
        fallback={
          <div className={styles.emptyContainer}>
            <p>{!!searchValue ? NO_RECORDS_FOUND : NO_RECORDS}</p>
          </div>
        }
      >
        <CustomTableWithPagination
          data={jobList?.data ?? []}
          columns={getDatabaseColumns({
            handleArchiveConfirmOpen: handleArchiveConfirmOpen,
            handleCustomerNavigate: handleCustomerProfileNavigate,
            handleBlockUnblockConfirm: handleBlockUnblockJob,
            jobSortValues: jobDatabaseSort,
          })}
          footer={getTableFooter}
          isClickableTable
          totalData={jobList?.meta?.totalRecords}
          currentPage={currentPage}
          pageSize={pageSize}
          handleChange={handlePageChange}
          handleSizeChangerChange={handlePageSizeChange}
          handleSelectedRow={handleJobProfileNavigate}
          isSortable={true}
          handleSorter={handleSorterAction}
        />
      </ConditionalRender>
    </>
  );
};

export default JobDatabaseTable;
